import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import merge from 'lodash.merge';
import { Formik, FormikHelpers, FormikProps, Form } from 'formik';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';

import GridSubtitle from 'components/GridSubtitle/GridSubtitle';
import compose from 'helpers/compose';
import { scrollToTop } from 'helpers/helpers';
import Background from 'components/Layout/Background';
import FormSelectField from 'components/FormFields/FormSelectField';
import FormNumberField from 'components/FormFields/FormNumberField';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import Info from 'components/Tooltip/Info';
import { MultiInputError } from 'components/MultiInputError/MultiInputError';

import { CalculationMode } from 'common/interfaces';
import { Partner } from 'stores/workflowStores/finances/planningFor/model';
import { RetirementInterface, getInitialValues } from 'stores/workflowStores/finances/retirement/model';
import { WorkflowRetirementStore } from 'stores/workflowStores/finances/retirement/workflowRetirementStore';
import { AccountEditBasic } from 'stores/workflowStores/workflowBasicStore/model';
import { RootStore } from 'stores/rootStore';
import { useTwoColumns } from 'hooks/useTwoColumns';
import OtherIncome from './OtherIncome';
import { Currency } from 'enums/index';
import { RentModalsStore } from 'stores/workflowStores/finances/rentModalsStore';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  accountId: string;
  partner: Partner;
  rootStore: RootStore;
  basicData: AccountEditBasic;
  currency: Currency;
  rentModalsStore: RentModalsStore;
  detailMode?: boolean;
}

const Retirement: React.FC<Props> = ({
  accountId,
  rootStore,
  partner,
  basicData,
  currency,
  rentModalsStore,
  detailMode,
}) => {
  const { t } = useTranslation();
  const [store] = useState(new WorkflowRetirementStore(rootStore, accountId, rentModalsStore, detailMode));
  const { data, calcTotalIncome, handleSubmit } = store;
  const translatedCurrency = t(`currency_${currency}`);
  const { rentFormKey, onNextRentTab, setRentSubmitting } = rentModalsStore; //rentRedirect
  const { twoColumns } = useTwoColumns();
  const initialValues = getInitialValues(!!partner, currency);

  useEffect(() => {
    scrollToTop('.MuiDialogContent-root');
    store.fetchRetirement();
  }, [store]);

  const renderDesiredPensionForm = (formikProps: FormikProps<RetirementInterface>, forPartner = false) => {
    const target = forPartner ? 'partnerData' : 'clientData';
    const retirementAgeError = get(formikProps, `errors.${target}.plannedRetirementAge`);

    const header = (text?: string) => (
      <GridSubtitle typographyProps={{ variant: 'h2' }}>
        {text || (forPartner ? partner.firstname : get(basicData, 'client.firstname'))}
      </GridSubtitle>
    );

    if ((forPartner && partner.retired) || (!forPartner && basicData.client.retired)) {
      return (
        <Grid item container>
          {header()}
          <GridSubtitle>
            {t(`finQuestionnary_pension_${forPartner ? 'partner' : 'client'}AlreadyInPension`)}
          </GridSubtitle>
          <Grid item xs={6}>
            <FormNumberField
              name={`${target}.plannedPension.value`}
              label={t('finQuestionnary_pension_plannedPension')}
              endAdornment={translatedCurrency}
              readOnly
            />
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid item container>
        {header()}

        {formikProps.values[target]!.calculationMode !== CalculationMode.NONE && (
          <>
            <GridSubtitle>
              {t('finQuestionnary_pension_monthlyFromAge')}
              <Info content={t('tooltip_retirement_mothlyFromAge')} heading />
            </GridSubtitle>
            <Grid item xs={6}>
              <FormNumberField
                name={`${target}.plannedRetirementAge.years`}
                label={t('general_years')}
                afterBlur={formikProps.submitForm}
                error={!!retirementAgeError}
                fast={false}
                maxLength={2}
              />
            </Grid>
            <Grid item xs={6}>
              <FormNumberField
                name={`${target}.plannedRetirementAge.months`}
                label={t('general_months')}
                afterBlur={formikProps.submitForm}
                error={!!retirementAgeError}
                fast={false}
                maxLength={2}
              />
            </Grid>
            {retirementAgeError && !retirementAgeError.months && <MultiInputError error={t(retirementAgeError)} />}
          </>
        )}

        <GridSubtitle>
          {t('finQuestionnary_pension_desiredPension')}
          <Info content={t('tooltip_retirement_desiredPension')} heading />
          <a
            href="https://eportal.cssz.cz/web/portal/-/sluzby/informativni-duchodova-aplikace"
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: '16px' }}
          >
            {t('finQuestionnary_pension_link')}
          </a>
        </GridSubtitle>

        <Grid item xs={6}>
          <FormSelectField
            name={`${target}.calculationMode`}
            label={t('finQuestionnary_pension_guess')}
            options={formikProps.values.options?.calculationMode.map((mode) => ({
              value: mode,
              label: t(`calculationMode_${mode}`),
            }))}
            tooltipContent={t('tooltip_retirement_calculationMode')}
            afterChange={
              get(formikProps.values, `[${target}].calculationMode`) === CalculationMode.CALCULATED ||
              get(formikProps.values, `[${target}].calculationMode`) === CalculationMode.NONE
                ? formikProps.submitForm
                : () => {}
            }
          />
        </Grid>
        <Grid item xs={6}>
          {get(formikProps.values, `[${target}].calculationMode`) === CalculationMode.MANUAL && (
            <FormNumberField
              name={`${target}.plannedPension.value`}
              label={t('finQuestionnary_pension_retirement')}
              endAdornment={translatedCurrency}
              afterBlur={formikProps.submitForm}
            />
          )}

          {formikProps.values[target]!.calculationMode === CalculationMode.CALCULATED && (
            <FormNumberField
              name={`${target}.calculatedPension.value`}
              label={t('finQuestionnary_pension_retirement')}
              endAdornment={translatedCurrency}
              afterBlur={formikProps.submitForm}
              readOnly
            />
          )}
        </Grid>

        {formikProps?.values?.[target]?.insuranceRentCalculationMode && (
          <>
            <GridSubtitle>{t('finQuestionnary_pension_DPSrentCount')}</GridSubtitle>
            <Grid item xs={6}>
              <TextField
                label={t('finQuestionnary_pension_guess')}
                value={t(`calculationMode_${formikProps.values[target]?.insuranceRentCalculationMode}`)}
                inputProps={{ readOnly: true }}
              />
            </Grid>
            {formikProps?.values?.[target]?.insuranceRentCalculationMode !== 'NONE' && (
              <Grid item xs={6}>
                <FormNumberField
                  name={`${target}.insuranceRent.value`}
                  label={t('general_rent_monthly')}
                  endAdornment={translatedCurrency}
                  readOnly
                />
                <TextField
                  name={`${target}.insuranceRent.currency`}
                  type="hidden"
                  style={{ visibility: 'hidden' }}
                  value={currency}
                />
              </Grid>
            )}
            {formikProps?.values[target]?.insuranceRentCalculationMode === 'NONE' && <Grid item xs={6} />}
          </>
        )}
      </Grid>
    );
  };

  const submit = async (values: RetirementInterface, formikHelpers: FormikHelpers<RetirementInterface>) => {
    // if there is only client and he is retired or if both client and partner are retired and there is no extra income
    // do not even submit and go to nextRentTab right away
    if (
      ((partner?.retired && basicData.client.retired) || (basicData.client.retired && !partner)) &&
      (values.extraIncome?.length === 0 || !values.extraIncome)
    ) {
      onNextRentTab();
      return;
    }
    setRentSubmitting(true);
    await handleSubmit(values, formikHelpers);
    setRentSubmitting(false);
  };

  return (
    data && (
      <Formik initialValues={merge({}, initialValues, data)} onSubmit={submit} enableReinitialize>
        {(formikProps) => (
          <FormWithoutEnterSubmit id={rentFormKey}>
            <Background inside>
              <div className={twoColumns}>
                {basicData && renderDesiredPensionForm(formikProps)}
                {partner && renderDesiredPensionForm(formikProps, true)}
              </div>
              <Grid container style={{ marginTop: 24 }}>
                <Grid item xs={12}>
                  <Typography>
                    {t('finQuestionnary_pension_countPensionIncome')}
                    <Info content={t('tooltip_retirement_otherIncomes')} heading />
                  </Typography>
                </Grid>
                <OtherIncome accountId={accountId} calcTotalIncome={calcTotalIncome} currency={currency} />
              </Grid>
              <Typography style={{ marginTop: 24, marginBottom: 16 }}>
                {t('finQuestionnary_pension_statedMonthlyIncomes')}
              </Typography>
              <FormNumberField
                style={{ marginLeft: '25%', width: 'calc(25% - 18px)' }}
                name="totalIncome.value"
                endAdornment={translatedCurrency}
                label={t('finQuestionnary_pension_monthAmount')}
                readOnly
              />
            </Background>
          </FormWithoutEnterSubmit>
        )}
      </Formik>
    )
  );
};

export default compose(inject('rootStore'), observer)(Retirement);
